import React, { useMemo } from "react";
import { observer } from "mobx-react";
import Calendar from "./Calendar";
import { useCalendarController } from "./controller/CalendarController";

import { useCalendarDraftEvents } from "./useCalendarDraftEvents";
import useStore from "../../helpers/useStore";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";

/**
 * CalendarWithController - A wrapper component that uses the CalendarController hook
 * to manage all calendar state and data, then passes the prepared props to the Calendar component.
 *
 * This component removes the need for parent components to manage calendar-specific state
 * and provides a clean separation between data management and UI rendering.
 */
const CalendarWithController: React.FC = observer(() => {
  const controller = useCalendarController();
  // const { selectedPatientId } = useStore(CalendarContext);
  // const locationId = useLocationId();

  // const scope: MutationScope | undefined = useMemo(
  //   () =>
  //     selectedPatientId && locationId
  //       ? { patientId: String(selectedPatientId), locationId }
  //       : undefined,
  //   [selectedPatientId, locationId],
  // );

  if (!controller) {
    return <div>Loading...</div>; // or some loading indicator
  }
  const { data, handlers } = controller;

  // Merge draft events (shift/placement previews) from the SchedulerAgent store
  // Backplanning agent results are regular time slots → always show on main calendar.
  // Rescheduling agent results also go on main calendar. Hide drafts only in continuous view.
  const { continuousView } = useStore(CalendarContext);
  const showDrafts = !continuousView;
  const draftEvents = useCalendarDraftEvents();
  const mergedEvents = useMemo(
    () =>
      showDrafts && draftEvents.length > 0
        ? [...data.calendarEvents, ...draftEvents]
        : data.calendarEvents,
    [data.calendarEvents, draftEvents, showDrafts],
  );

  return (
    <>
      <Calendar
        // Core calendar data
        // Pre-adapted flat calendar events (new prop for refactor)
        calendarEvents={mergedEvents}
        // Patient and drawer data
        selectedShelfAppointment={data.selectedShelfAppointment}
        patientAvailabilities={data.patientAvailabilities}
        patientUnavailableTypes={data.patientUnavailableTypes}
        // Users and resources (resources generated by controller, users for backward compatibility)
        users={data.users}
        resources={data.resources}
        // UI state
        date={data.selectedDate}
        isContinuous={data.isContinuousView}
        isLoading={data.isLoading}
        // Shelf events UI mode managed inside CalendarStore
        // isShelfEvents={calendarStore.isShelfEvents}
        lockedAppointments={data.lockedAppointments}
        sublocalities={data.sublocalities}
        headerWidth={data.headerWidth}
        // Event handlers
        onDatesSet={handlers.onDatesSet}
        onCreateEvent={handlers.onCreateEvent}
        onChangeEvent={handlers.onChangeEvent as any}
        onEventReceive={handlers.onEventReceive}
        onRemoveEvent={handlers.onRemoveEvent}
        onRemoveGroupTherapist={handlers.onRemoveGroupTherapist}
        onRemoveContinuousEvent={handlers.onRemoveContinuousEvent}
        onOutdated={handlers.onOutdated}
        onContinuousViewChange={handlers.onContinuousViewChange}
        onCancelledViewChange={handlers.onCancelledViewChange}
        onMoveEventDate={handlers.onMoveEventDate as any}
        onAddShelfEvent={handlers.onAddShelfEvent as any}
        onEventContentClick={handlers.onEventContentClick}
        onCloseNotesDialog={handlers.onCloseNotesDialog}
        onSelectedShelfEventReceive={handlers.onSelectedShelfEventReceive}
        groupCheck={data.groupCheck}
        cellSelection={data.focusPosition || undefined}
      />

    </>
  );
});
CalendarWithController.displayName = "CalendarWithController";
export default CalendarWithController;
